import { CalendarItem } from '@wui/common-library'
import { AnyConferenceChangedByOtherEvent, GroupStatusEvent, PServed, useIM } from '@wui/im'
import dayjs from 'dayjs'
import { defineStore, storeToRefs } from 'pinia'
import { IcsCalendar, IcsEvent, convertIcsCalendar } from 'ts-ics'
import { computed, ref } from 'vue'

import { useCalendarStore } from '@/entities/calendar'
import { useStoreContacts } from '@/entities/contact'
import { useRoom } from '@/entities/use-room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { getProfileTimezone } from '@/shared/composables/timezone'
import { isNotExpired } from '@/shared/utils/filters'
import sort from '@/shared/utils/sort'
import { fromPServedUser } from '@/shared/utils/uri'

import {
  generateCalendarItems,
  hydrateScheduleCalendarAttendees,
  isAttendeeMatchingInternalNumber,
  isOrganizerAttendee,
  parseWuiIcsCalendar,
  sanitizeCorruptedIcsString,
  SCHEDULE_EVENT_ID_SEPARATOR,
  scheduleIcsNonStandardParseOptions,
  toScheduleOrganizerIdentity,
  withInitialIcsRevision,
  withNextIcsRevision,
  withOrganizerAsAcceptedAttendee,
} from './lib'
import { getDefaultCalendar } from './utils'

interface RoomScheduleByRoomGuid {
  [roomPServed: string]: Array<IcsEvent>
}

const { byTimestamp } = sort.chatEvents

export const useRoomScheduleStore = defineStore('room-schedule', () => {
  const { selectedMonth, selectedYear } = storeToRefs(useCalendarStore())
  const { enableSchedule, disableSchedule, updateSchedule } = useRoom()
  const { currentUser } = useCurrentUser()
  const { t } = useLocalization()

  const resolveOrganizerIdentity = () => toScheduleOrganizerIdentity(currentUser.value)

  const isLoading = ref(false)
  const scheduleByRoomPServed = ref<RoomScheduleByRoomGuid>({})
  const calendarsByPServed = ref(new Map<PServed, Map<IcsEvent['uid'], IcsCalendar>>())
  /** UID отменённых событий (`pServed` + separator + uid) — чтобы карточки create/update в истории показывали «отменено». */
  const cancelledScheduleIds = ref(new Set<string>())
  const from = computed(() => dayjs.tz(
    `${+selectedYear.value}-${+selectedMonth.value + 1}-01`,
    getProfileTimezone(),
  ).startOf('month').unix())
  const to = computed(() => dayjs.tz(
    `${+selectedYear.value}-${+selectedMonth.value + 1}-01`,
    getProfileTimezone(),
  ).endOf('month').unix())
  const monthItemsByPServed = computed<Map<string, CalendarItem[]>>(() => {
    const result = new Map()
    /** TODO WUI-2148
     * map recurrenceRule to month objects for monthItemsByPServed
     * */
    Object
      .keys(scheduleByRoomPServed.value)
      .flatMap((pServed) => scheduleByRoomPServed.value[pServed]?.map((event) => ({ pServed, event })) ?? [])
      .forEach(({ pServed, event }) => {
        let items = []
        const item = generateCalendarItems(
          from.value,
          to.value,
          pServed,
          event,
          t('common.withoutTitle'),
        )
        if (result.has(pServed)) {
          items = [...result.get(pServed), ...item]
        } else {
          items = [...item]
        }

        result.set(pServed, items)
      })
    return result
  })

  const monthItems = computed(() => {
    return Array.from(monthItemsByPServed.value.values()).flatMap((items) => items)
      .sort((a, b) => byTimestamp(b, a))
  })

  const monthItemsNotExpired = computed(() => monthItems.value.filter(isNotExpired))

  const findByPServed = (pServed: PServed) => {
    return scheduleByRoomPServed.value[pServed]
  }

  const findScheduleById = (id: string) => {
    const [pServed, eventId] = id.split(SCHEDULE_EVENT_ID_SEPARATOR)
    const schedule = calendarsByPServed.value.get(pServed ?? '')
    if (!schedule) {
      return
    }

    const calendar = schedule.get(eventId ?? '')
    return calendar ? hydrateScheduleCalendarAttendees(calendar) : undefined
  }

  const parseIncomingCalendar = (
    pServed: PServed,
    uid: string,
    ical: IcsCalendar | string,
  ): IcsCalendar | undefined => {
    if (ical && typeof ical === 'object') {
      return hydrateScheduleCalendarAttendees(ical)
    }

    if (typeof ical !== 'string') {
      const cached = calendarsByPServed.value.get(pServed)?.get(uid)
      return cached ? hydrateScheduleCalendarAttendees(cached) : undefined
    }

    const sanitized = sanitizeCorruptedIcsString(ical)
    try {
      return hydrateScheduleCalendarAttendees(parseWuiIcsCalendar(sanitized))
    } catch {
      // Fallback: stock z.email() в старых @wui/im отклоняет MAILTO:tel:...
      try {
        return hydrateScheduleCalendarAttendees(
          convertIcsCalendar(undefined, sanitized, {
            nonStandard: scheduleIcsNonStandardParseOptions,
          }),
        )
      } catch {
        const cached = calendarsByPServed.value.get(pServed)?.get(uid)
        return cached ? hydrateScheduleCalendarAttendees(cached) : undefined
      }
    }
  }

  const markScheduleCancelled = (pServed: PServed, eventUid: string) => {
    const key = `${pServed}${SCHEDULE_EVENT_ID_SEPARATOR}${eventUid}`
    if (cancelledScheduleIds.value.has(key)) {
      return
    }
    cancelledScheduleIds.value = new Set(cancelledScheduleIds.value).add(key)
  }

  const removeActiveSchedule = (pServed: PServed, eventUid: string) => {
    markScheduleCancelled(pServed, eventUid)
    calendarsByPServed.value.get(pServed)?.delete(eventUid)
    if (scheduleByRoomPServed.value[pServed]) {
      scheduleByRoomPServed.value[pServed] = scheduleByRoomPServed.value[pServed]
        .filter(({ uid }) => uid !== eventUid)
    }
  }

  /** Событие отменено: явно в cancelled set или уже нет в активных расписаниях комнаты (store загружен). */
  const isEventCancelled = (pServed: PServed, eventUid: string) => {
    const key = `${pServed}${SCHEDULE_EVENT_ID_SEPARATOR}${eventUid}`
    if (cancelledScheduleIds.value.has(key)) {
      return true
    }

    const hasRoomState = calendarsByPServed.value.has(pServed)
      || Object.prototype.hasOwnProperty.call(scheduleByRoomPServed.value, pServed)
    if (!hasRoomState) {
      return false
    }

    return !(scheduleByRoomPServed.value[pServed] ?? []).some(({ uid }) => uid === eventUid)
  }

  const updateFromContactStatus = (contactStatus: GroupStatusEvent) => {
    const { extendedParams: { roomSchedules = new Map() }, pServed } = contactStatus

    const incomingEntries: Array<[string, IcsCalendar | string]> = roomSchedules instanceof Map
      ? [...roomSchedules.entries()]
      : Object.entries(roomSchedules as { [guid: string]: IcsCalendar | string })

    const previousCalendars = calendarsByPServed.value.get(pServed)

    if (incomingEntries.length === 0) {
      previousCalendars?.forEach((_, uid) => markScheduleCancelled(pServed, uid))
      // Пустой список = комната «известна», активных событий нет (карточки в истории → cancelled)
      calendarsByPServed.value.set(pServed, new Map())
      scheduleByRoomPServed.value[pServed] = []
      return
    }

    const schedules = new Map<string, IcsCalendar>()
    const incomingUids = new Set(incomingEntries.map(([uid]) => uid))

    incomingEntries.forEach(([uid, ical]) => {
      const parsed = parseIncomingCalendar(pServed, uid, ical)
      if (parsed) {
        schedules.set(uid, parsed)
        return
      }
      // Парс не удался, но uid пришёл с сервера — сохраняем предыдущую копию
      const previous = previousCalendars?.get(uid)
      if (previous) {
        schedules.set(uid, previous)
      }
    })

    if (schedules.size === 0) {
      return
    }

    previousCalendars?.forEach((_, uid) => {
      // Отмена только если сервер явно убрал uid из roomSchedules
      if (!incomingUids.has(uid)) {
        markScheduleCancelled(pServed, uid)
      }
    })

    calendarsByPServed.value.set(pServed, schedules)
    const events = [...(schedules.values() || [])].flatMap(({ events = [] }) => events)
    scheduleByRoomPServed.value[pServed] = events
  }

  const _updateFromCommandAcceptedEx = (pServed: string, event: IcsEvent) => {
    if (!scheduleByRoomPServed.value[pServed]) {
      scheduleByRoomPServed.value[pServed] = []
    }
    const idx = scheduleByRoomPServed.value[pServed].findIndex(({ uid }) => uid === event.uid)
    if (idx >= 0) {
      scheduleByRoomPServed.value[pServed][idx] = event
    } else {
      scheduleByRoomPServed.value[pServed].push(event)
    }

    const calendars = new Map(calendarsByPServed.value.get(pServed) ?? [])
    const identity = resolveOrganizerIdentity()
    calendars.set(event.uid, getDefaultCalendar(event, {
      includeSequence: true,
      ...(identity ? { identity } : {}),
    }))
    calendarsByPServed.value.set(pServed, calendars)
  }

  const resolvePreviousSequence = (pServed: string, event: IcsEvent): number => (
    calendarsByPServed.value.get(pServed)?.get(event.uid)?.events?.[0]?.sequence
    ?? event.sequence
    ?? 0
  )

  const enable = async (pServed: string, event: IcsEvent) => {
    isLoading.value = true
    const includeCreatorAsAttendee = Boolean(
      event.attendees?.some((attendee) => isOrganizerAttendee(attendee, undefined, resolveOrganizerIdentity())),
    )
    const normalizedEvent = withInitialIcsRevision(
      withOrganizerAsAcceptedAttendee(
        event,
        resolveOrganizerIdentity(),
        { includeCreatorAsAttendee },
      ),
    )
    return enableSchedule(pServed, normalizedEvent)
      .then(() => {
        _updateFromCommandAcceptedEx(pServed, normalizedEvent)
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const update = async (pServed: string, event: IcsEvent) => {
    isLoading.value = true
    const includeCreatorAsAttendee = Boolean(
      event.attendees?.some((attendee) => isOrganizerAttendee(attendee, undefined, resolveOrganizerIdentity())),
    )
    const previousSequence = resolvePreviousSequence(pServed, event)
    const normalizedEvent = withNextIcsRevision(
      withOrganizerAsAcceptedAttendee(
        { ...event, sequence: previousSequence },
        resolveOrganizerIdentity(),
        { includeCreatorAsAttendee },
      ),
    )
    return updateSchedule(pServed, normalizedEvent)
      .then(() => {
        _updateFromCommandAcceptedEx(pServed, normalizedEvent)
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const clearByPServed = (pServed: PServed) => {
    calendarsByPServed.value.get(pServed)?.forEach((_, uid) => markScheduleCancelled(pServed, uid))
    calendarsByPServed.value.delete(pServed)
    delete scheduleByRoomPServed.value[pServed]
  }

  const removeMemberFromSchedules = (pServed: PServed, targetInternalNumber: string) => {
    const roomCalendars = calendarsByPServed.value.get(pServed)
    if (!roomCalendars) {
      return
    }

    const { findByPServed: findContactByImLogin } = useStoreContacts()
    const updatedCalendars = new Map(roomCalendars)
    let hasChanges = false

    updatedCalendars.forEach((calendar, uid) => {
      const event = calendar.events?.[0]
      if (!event?.attendees?.length) {
        return
      }

      const attendees = event.attendees.filter((attendee) => !isAttendeeMatchingInternalNumber(
        attendee,
        targetInternalNumber,
        findContactByImLogin,
      ))

      if (attendees.length === event.attendees.length) {
        return
      }

      hasChanges = true
      updatedCalendars.set(uid, {
        ...calendar,
        events: [{ ...event, attendees }],
      })
    })

    if (!hasChanges) {
      return
    }

    calendarsByPServed.value.set(pServed, updatedCalendars)
    scheduleByRoomPServed.value[pServed] = [...updatedCalendars.values()]
      .flatMap(({ events = [] }) => events)
  }

  const handleConferenceEdited = (event: AnyConferenceChangedByOtherEvent) => {
    if (event.type === 'ConferenceRemoved') {
      clearByPServed(event.pServed)
      return
    }

    if (event.type === 'ConferenceScheduleDisabled') {
      const uid = event.schedule?.events?.[0]?.uid
      if (uid) {
        removeActiveSchedule(event.pServed, uid)
      }
      return
    }

    if (event.type !== 'ConferenceMemberRemoved') {
      return
    }

    const targetNumber = event.targetNumber ?? fromPServedUser(event.executor)
    const { currentUser } = useCurrentUser()
    const isCurrentUserRemoved = targetNumber === currentUser.value?.internalNumber

    if (isCurrentUserRemoved) {
      clearByPServed(event.pServed)
      return
    }

    removeMemberFromSchedules(event.pServed, targetNumber)
  }

  const disable = async (pServed: string, eventUid: string) => {
    isLoading.value = true
    const calendar = calendarsByPServed.value.get(pServed)?.get(eventUid)
    if (!calendar) {
      isLoading.value = false
      return
    }
    return disableSchedule(pServed, calendar)
      .then(() => {
        removeActiveSchedule(pServed, eventUid)
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const { onConferenceUpdated, onConferenceEdited } = useIM()

  onConferenceUpdated((event: GroupStatusEvent) => {
    updateFromContactStatus(event)
  })

  onConferenceEdited(handleConferenceEdited)

  return {
    findByPServed,
    findScheduleById,
    calendarsByPServed,
    scheduleByRoomPServed,
    cancelledScheduleIds,
    isEventCancelled,
    markScheduleCancelled,
    updateFromContactStatus,
    clearByPServed,
    removeMemberFromSchedules,
    enable,
    disable,
    monthItems,
    monthItemsNotExpired,
    from,
    to,
    isLoading,
    update,
  }
})
